import { useState, useEffect, useCallback } from 'react'
import { apiClient } from '../services/api'
import { useTranslation } from 'react-i18next'
import { useToast } from '../components/shared/Toast'
import { Modal } from '../components/shared/Modal'

interface SubUser {
  rowKey: string
  displayName: string
  phone: string
  status: string
  createdAt: string
  activatedAt: string
}

interface SubUsersResponse {
  items: SubUser[]
}

export function SubUsersPage() {
  const { t } = useTranslation()
  const { showToast } = useToast()
  const [data, setData] = useState<SubUser[]>([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [editingEmail, setEditingEmail] = useState<string | null>(null)
  const [form, setForm] = useState({ email: '', displayName: '', phone: '' })
  const [submitting, setSubmitting] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    apiClient.get<SubUsersResponse>('/api/user/subusers')
      .then(r => setData(r.items))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  async function addSubUser() {
    setSubmitting(true)
    try {
      await apiClient.post('/api/user/subusers', form)
      showToast(t('subUsers.added'), 'success')
      setShowAdd(false)
      setForm({ email: '', displayName: '', phone: '' })
      load()
    } catch {
      showToast(t('errors.generic'), 'error')
    } finally {
      setSubmitting(false)
    }
  }

  async function updateSubUser(email: string) {
    setSubmitting(true)
    try {
      // Use the direct sub-user add endpoint which also handles updates via re-add
      await apiClient.post('/api/user/subusers', {
        email,
        displayName: form.displayName,
        phone: form.phone,
      })
      showToast(t('subUsers.updated'), 'success')
      setEditingEmail(null)
      setForm({ email: '', displayName: '', phone: '' })
      load()
    } catch {
      showToast(t('errors.generic'), 'error')
    } finally {
      setSubmitting(false)
    }
  }

  async function removeSubUser(email: string) {
    if (!window.confirm(t('contracts.removeConfirm'))) return
    try {
      await apiClient.delete(`/api/user/subusers/${encodeURIComponent(email)}`)
      showToast(t('subUsers.removed'), 'success')
      load()
    } catch {
      showToast(t('errors.generic'), 'error')
    }
  }

  async function reactivateSubUser(email: string) {
    try {
      const su = data.find(s => s.rowKey === email)
      await apiClient.post('/api/user/subusers', {
        email,
        displayName: su?.displayName ?? '',
        phone: su?.phone ?? '',
      })
      showToast(t('subUsers.reactivated'), 'success')
      load()
    } catch {
      showToast(t('errors.generic'), 'error')
    }
  }

  function openEdit(su: SubUser) {
    setEditingEmail(su.rowKey)
    setForm({ email: su.rowKey, displayName: su.displayName, phone: su.phone ?? '' })
  }

  if (loading) return <p className="text-brand-500 py-8 text-center">{t('subUsers.loading')}</p>

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-brand-700">{t('subUsers.title')}</h1>
      </div>
      <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm px-4 py-3 rounded-lg">
        {t('subUsers.description')}
      </div>
      <div className="flex items-center justify-end">
        <button
          onClick={() => {
            setShowAdd(true)
            setForm({ email: '', displayName: '', phone: '' })
          }}
          className="bg-brand-500 text-white text-sm px-4 py-2 rounded-lg hover:bg-brand-600 transition-colors"
        >
          {t('subUsers.add')}
        </button>
      </div>

      {data.length === 0 ? (
        <p className="text-brand-400">{t('subUsers.noSubUsers')}</p>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="divide-y divide-brand-50">
            {data.map(su => (
              <div key={su.rowKey} className="px-4 py-3 flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-brand-700">{su.displayName}</p>
                  <p className="text-xs text-brand-400 mt-0.5">{su.rowKey}</p>
                  {su.phone && (
                    <p className="text-xs text-brand-400 mt-0.5">{su.phone}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    su.status === 'active' ? 'bg-green-50 text-green-700' :
                    su.status === 'pending' ? 'bg-amber-50 text-amber-700' :
                    'bg-gray-100 text-gray-500'
                  }`}>
                    {su.status}
                  </span>
                  {su.status !== 'inactive' && (
                    <>
                      <button
                        onClick={() => openEdit(su)}
                        className="text-brand-500 text-xs px-2 py-1 rounded hover:bg-brand-50 transition-colors"
                      >
                        {t('subUsers.edit')}
                      </button>
                      <button
                        onClick={() => removeSubUser(su.rowKey)}
                        className="text-red-500 text-xs px-2 py-1 rounded hover:bg-red-50 transition-colors"
                      >
                        {t('subUsers.deactivate')}
                      </button>
                    </>
                  )}
                  {su.status === 'inactive' && (
                    <button
                      onClick={() => reactivateSubUser(su.rowKey)}
                      className="text-green-600 text-xs px-2 py-1 rounded hover:bg-green-50 transition-colors"
                    >
                      {t('subUsers.activate')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Sub-User Modal */}
      <Modal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        title={t('subUsers.add')}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-brand-700 mb-1">{t('subUsers.email')}</label>
            <input
              type="email"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-brand-700 mb-1">{t('subUsers.name')}</label>
            <input
              type="text"
              value={form.displayName}
              onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-brand-700 mb-1">{t('subUsers.phone')}</label>
            <input
              type="tel"
              value={form.phone}
              onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              onClick={addSubUser}
              disabled={submitting || !form.email || !form.displayName}
              className="bg-brand-500 text-white text-sm px-5 py-2 rounded-lg hover:bg-brand-600 disabled:opacity-50 transition-colors"
            >
              {t('subUsers.add')}
            </button>
            <button
              onClick={() => setShowAdd(false)}
              className="text-brand-500 text-sm px-5 py-2 rounded-lg hover:bg-brand-50 transition-colors"
            >
              {t('admin.users.cancel')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Sub-User Modal */}
      <Modal
        open={!!editingEmail}
        onClose={() => setEditingEmail(null)}
        title={t('subUsers.edit')}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-brand-700 mb-1">{t('subUsers.email')}</label>
            <input
              type="email"
              value={form.email}
              disabled
              className="w-full rounded-lg border-brand-100 text-sm bg-gray-50 text-brand-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-brand-700 mb-1">{t('subUsers.name')}</label>
            <input
              type="text"
              value={form.displayName}
              onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-brand-700 mb-1">{t('subUsers.phone')}</label>
            <input
              type="tel"
              value={form.phone}
              onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => editingEmail && updateSubUser(editingEmail)}
              disabled={submitting || !form.displayName}
              className="bg-brand-500 text-white text-sm px-5 py-2 rounded-lg hover:bg-brand-600 disabled:opacity-50 transition-colors"
            >
              {t('admin.users.save')}
            </button>
            <button
              onClick={() => setEditingEmail(null)}
              className="text-brand-500 text-sm px-5 py-2 rounded-lg hover:bg-brand-50 transition-colors"
            >
              {t('admin.users.cancel')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
